import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  BIOCHAR_PRODUCTION_IMAGE,
  Reveal,
  RURAL_INCOME_IMAGE,
  SOIL_HEALTH_IMAGE,
  WORK_IMAGE,
} from "./shared";

const ITEMS = [
  {
    label: "Biochar production",
    body: "We transform crop residue into stable biochar through skillful production, locking carbon away for centuries.",
    testId: "what-we-do-item-1",
    image: BIOCHAR_PRODUCTION_IMAGE,
    imageAlt: "Farmer-owners tending a flaming biochar kiln, coastal Odisha",
    imagePosition: "center 60%",
  },
  {
    label: "Soil health",
    body: "Returning biochar back to the farmlands improves soil organic carbon, water retention capacity and crop yield.",
    testId: "what-we-do-item-2",
    image: SOIL_HEALTH_IMAGE,
    imageAlt: "Rows of crops at golden hour beside a neem tree, coastal Odisha",
    imagePosition: "center 55%",
  },
  {
    label: "Rural prosperity",
    body: "Carbon finance flows to the farmer-owned organisations who do the work.",
    testId: "what-we-do-item-3",
    image: RURAL_INCOME_IMAGE,
    imageAlt: "Farmer-owners gathering crop residue in a misty field, coastal Odisha",
    imagePosition: "center 55%",
  },
];

export const WhatWeDoSection = React.forwardRef(function WhatWeDoSection(_, ref) {
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const contentOpacity = useTransform(scrollYProgress, [0, 0.55, 1], [1, 1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      id="work"
      ref={ref}
      className="relative w-full min-h-screen overflow-hidden snap-start flex items-center py-20 md:py-24 px-6 md:px-12"
      data-testid="what-we-do-section"
    >
      {/* Full-bleed background image with darken overlay for legibility */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={WORK_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 50%" }}
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(8,12,8,0.78) 0%, rgba(8,12,8,0.72) 50%, rgba(8,12,8,0.82) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.28) 75%)",
          }}
        />
      </div>

      <motion.div
        className="relative w-full max-w-7xl mx-auto"
        style={{ opacity: contentOpacity, y: contentY }}
      >
        <Reveal
          as="div"
          className="text-[11px] tracking-[0.28em] uppercase text-white/75 mb-14 md:mb-20 font-sans font-semibold text-center"
        >
          What we do
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10 lg:gap-16">
          {ITEMS.map((item, idx) => (
            <Reveal
              key={item.label}
              delay={idx * 0.12}
              y={42}
              duration={1.15}
              className="group"
              data-testid={item.testId}
            >
              <div className="overflow-hidden">
                <div className="transition-transform duration-[1200ms] ease-out group-hover:scale-[1.025]">
                  <div
                    className="relative overflow-hidden aspect-[4/3] bg-[#EDE7D8]"
                    data-testid={`image-${item.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    <img
                      src={item.image}
                      alt={item.imageAlt || ""}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ objectPosition: item.imagePosition || "center" }}
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
              <h3
                className="mt-7 md:mt-8 font-serif text-2xl md:text-[1.75rem] leading-tight font-light text-white"
                style={{ textShadow: "0 2px 24px rgba(0,0,0,0.4)" }}
              >
                {item.label}
              </h3>
              <p className="mt-4 text-white/85 text-base leading-[1.7] font-sans font-light">
                {item.body}
              </p>
            </Reveal>
          ))}
        </div>
      </motion.div>
    </section>
  );
});
